import type { OverviewMonth } from '@/types/table';

// Financial years run April to March and are named '2026-27'. Months are 'YYYY-MM'.

export function fyOf(date: Date): string {
  const start = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(2)}`;
}

export function fyOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return fyOf(new Date(y, m - 1, 1));
}

export function fyMonths(fy: string): string[] {
  const start = Number(fy.slice(0, 4));
  const pad = (m: number) => String(m).padStart(2, '0');
  return [
    ...[4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => `${start}-${pad(m)}`),
    ...[1, 2, 3].map((m) => `${start + 1}-${pad(m)}`),
  ];
}

export function fyLabel(fy: string): string {
  const start = Number(fy.slice(2, 4));
  return `FY ${fy} (Apr ${start} – Mar ${start + 1})`;
}

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthShort = (month: string) => SHORT[Number(month.slice(5)) - 1];
export const monthLong = (month: string) => `${LONG[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`;
export const monthShortYear = (month: string) => `${monthShort(month)} ${month.slice(0, 4)}`;

export const incomeOf = (m: OverviewMonth) => m.clientIncome + m.otherIncome;
/** A refund is money back on something we spent, so it reduces expenses. */
export const expensesOf = (m: OverviewMonth) => m.expenses - m.refunds;

const round2 = (n: number) => Math.round(n * 100) / 100;

function mergeRanked(lists: Array<Array<{ name: string; amount: number }>>) {
  const totals = new Map<string, number>();
  for (const list of lists) for (const { name, amount } of list) totals.set(name, (totals.get(name) ?? 0) + amount);
  return [...totals.entries()].map(([name, amount]) => ({ name, amount: round2(amount) })).sort((a, b) => b.amount - a.amount);
}

export interface PeriodSummary {
  clientIncome: number;
  otherIncome: number;
  income: number;
  refunds: number;
  grossExpenses: number;
  expenses: number;
  result: number;
  openingBalance: number;
  closingBalance: number;
  clients: Array<{ name: string; amount: number }>;
  categories: Array<{ name: string; amount: number }>;
  nonOperating: Array<{ label: string; amount: number }>;
}

/** Totals for a run of consecutive months. Null when there is nothing in the period. */
export function summarise(months: OverviewMonth[]): PeriodSummary | null {
  if (months.length === 0) return null;
  const add = (pick: (m: OverviewMonth) => number) => round2(months.reduce((sum, m) => sum + pick(m), 0));
  const clientIncome = add((m) => m.clientIncome);
  const otherIncome = add((m) => m.otherIncome);
  const refunds = add((m) => m.refunds);
  const grossExpenses = add((m) => m.expenses);
  const income = round2(clientIncome + otherIncome);
  const expenses = round2(grossExpenses - refunds);
  return {
    clientIncome, otherIncome, income, refunds, grossExpenses, expenses,
    result: round2(income - expenses),
    openingBalance: months[0].openingBalance,
    closingBalance: months[months.length - 1].closingBalance,
    clients: mergeRanked(months.map((m) => m.clients)),
    categories: mergeRanked(months.map((m) => m.categories)),
    nonOperating: months.flatMap((m) => m.nonOperating),
  };
}

/** One axis, stepping in 1 / 2 / 5 × a power of ten, with at most five steps. */
export function niceAxis(peak: number): number[] {
  if (!(peak > 0)) return [0, 1];
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = [0.1, 0.2, 0.5, 1, 2, 5, 10].map((f) => f * magnitude).find((s) => Math.ceil(peak / s) <= 5) ?? magnitude * 10;
  const top = Math.ceil(peak / step);
  return Array.from({ length: top + 1 }, (_, i) => round2(i * step));
}

function currencySymbol(currency: string): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
    .formatToParts(0).find((part) => part.type === 'currency')?.value ?? '';
}

/** ₹1L and ₹50K for rupees; $100K and $1M for the rest. */
export function axisLabel(value: number, currency: string): string {
  if (value === 0) return '0';
  const symbol = currencySymbol(currency);
  if (currency === 'INR' && value >= 100000) return `${symbol}${value / 100000}L`;
  if (value >= 1000000) return `${symbol}${value / 1000000}M`;
  if (value >= 1000) return `${symbol}${value / 1000}K`;
  return `${symbol}${value}`;
}

/** Whole days from today to a due date, by calendar day. Negative means overdue. */
export function daysUntil(dueDate: string, now: Date = new Date()): number {
  const due = Date.parse(dueDate.slice(0, 10));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due - today) / 86400000);
}

export function dueLabel(days: number): string {
  if (days === 0) return 'Due today';
  const count = Math.abs(days);
  const unit = count === 1 ? 'day' : 'days';
  return days < 0 ? `Overdue ${count} ${unit}` : `Due in ${count} ${unit}`;
}

/** Sum per currency, rupees first. Currencies are never added together. */
export function totalsByCurrency<T extends { currency: string }>(items: T[], amount: (item: T) => number): Array<{ currency: string; amount: number }> {
  const totals = new Map<string, number>();
  for (const item of items) totals.set(item.currency, (totals.get(item.currency) ?? 0) + amount(item));
  return [...totals.entries()]
    .map(([currency, total]) => ({ currency, amount: round2(total) }))
    .sort((a, b) => (a.currency === 'INR' ? -1 : b.currency === 'INR' ? 1 : 0));
}
